/**
 * GstDisconnectTicker: the line under the top bar about GST portal sessions that
 * have died (noemailtick, then gsturgent, 26 Sep 2026).
 *
 * Founder, 26 Sep 2026: "show a moving notification on the top bar like news keeps
 * moving infact show it for all users", then, looking at it with twenty clients on
 * it: consultants "cant read it for all clients moving", so a press should open
 * "a urgent to do bucket which has everything in it".
 *
 * railonly, 27 Sep 2026 (founder: "sidebar itself should have everything"): the
 * fill rail now says all of it, with the "Act now" pill, so this line never says
 * it twice. It shows only where the rail is NOT on screen (full-screen pages):
 *
 * - **The office:** nothing. Its line only ever pointed at the rail's Urgent
 *   rows, and where there is no rail there was nothing for it to open.
 * - **A business's own people:** the moving line, about their own one or two
 *   businesses, in "you" words ("We emailed you the link").
 *
 * The list is `useDisconnectedGst()`, shared with the rail. Nothing shows while it
 * is empty, or before migration 20260926045048 is applied.
 */
import { AlertTriangle } from 'lucide-react';
import { useOfficeIdentity } from '@/shared/hooks/useOfficeIdentity';
import { ownLineItem, type DisconnectedClient } from '@/shared/gst/gstDisconnected';
import { openClientAt, useDisconnectedGst } from '@/shared/gst/useDisconnectedGst';

export function GstDisconnectTicker({ railHere }: { railHere: boolean }) {
    const { clients } = useDisconnectedGst();
    const { officeOrg } = useOfficeIdentity();
    if (clients.length === 0 || railHere || officeOrg) return null;
    return <OwnLine clients={clients} />;
}

function OwnLine({ clients }: { clients: DisconnectedClient[] }) {
    // Roughly the same reading speed however many there are.
    const seconds = Math.max(25, clients.length * 12);
    const items = clients.map(c => ({ c, ...ownLineItem(c) }));
    return (
        <div
            role="status"
            aria-label="Your GST portal connection has stopped"
            className="karr-ticker relative flex items-center h-7 shrink-0 overflow-hidden border-b border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/40 text-[12px] text-amber-900 dark:text-amber-200 z-20"
        >
            <span className="shrink-0 flex items-center gap-1.5 h-full pl-3 pr-2.5 font-semibold bg-amber-100 dark:bg-amber-900/40 border-r border-amber-200 dark:border-amber-900/50">
                <AlertTriangle size={13} aria-hidden />
                GST portal
            </span>
            <div className="karr-ticker-view min-w-0 flex-1 overflow-hidden">
                {/* Written twice so the loop never shows a gap; the copy is hidden
                    from screen readers, which read the first pass only. */}
                <div className="karr-ticker-track flex w-max" style={{ ['--karr-ticker-s' as string]: `${seconds}s` }}>
                    {[0, 1].map(pass => (
                        <div key={pass} className="flex shrink-0" aria-hidden={pass === 1 || undefined}>
                            {items.map(({ c, text, to, action }) => (
                                <button
                                    key={`${pass}-${c.org_id}-${c.gstin}`}
                                    type="button"
                                    tabIndex={pass === 1 ? -1 : undefined}
                                    onClick={() => openClientAt(c, to)}
                                    className="shrink-0 flex items-center gap-1.5 px-5 whitespace-nowrap hover:text-amber-950 dark:hover:text-amber-100"
                                >
                                    <span>{text}</span>
                                    <span className="font-semibold underline underline-offset-2">{action}</span>
                                </button>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
